var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useForm } from "react-hook-form";
import { moduleOptions, allPermissions } from "../data";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createNewRole, updateRole, getRolesDropdown, getRoleById, } from "@reducers/roles/thunk/role-thunk";
import { useTranslation } from "react-i18next";
import { toast } from "@ui/utils/toaster";
export const useRoleForm = ({ isEdit, role }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(["common"]);
    const [loading, setLoading] = useState(false);
    const methods = useForm({
        defaultValues: {
            name: (role === null || role === void 0 ? void 0 : role.name) || "",
            roleClaims: (role === null || role === void 0 ? void 0 : role.roleClaims) || [],
        },
    });
    const { control, handleSubmit, formState: { errors }, } = methods;
    const validationRules = {
        name: {
            required: "Role name is required",
            minLength: {
                value: 2,
                message: "Role name must be at least 2 characters",
            },
        },
    };
    const handleFormSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            const permissions = allPermissions.map((permission) => {
                var _a;
                const moduleClaim = (_a = data.roleClaims) === null || _a === void 0 ? void 0 : _a.find((claim) => claim.module === permission.key);
                let isAllowed = false;
                // Dashboard and AccessPoints View permission are always enabled
                if ((permission.key === "Dashboard" ||
                    permission.key === "AccessPoints") &&
                    permission.value.toLowerCase() === "view") {
                    isAllowed = true;
                }
                else if (moduleClaim) {
                    switch (permission.value.toLowerCase()) {
                        case "view":
                        case "read":
                            isAllowed = moduleClaim.viewPermission || false;
                            break;
                        case "create":
                            isAllowed = moduleClaim.createPermission || false;
                            break;
                        case "edit":
                        case "update":
                            isAllowed = moduleClaim.editPermission || false;
                            break;
                        case "delete":
                            isAllowed = moduleClaim.deletePermission || false;
                            break;
                        case "acknowledge":
                        case "clear":
                        case "generate":
                        case "export":
                        case "settings":
                        case "logs":
                            isAllowed = moduleClaim.viewPermission || false;
                            break;
                        case "serversettings":
                            isAllowed =
                                !!(moduleClaim === null || moduleClaim === void 0 ? void 0 : moduleClaim.editPermission) &&
                                    !!(moduleClaim === null || moduleClaim === void 0 ? void 0 : moduleClaim.serverSettingsPermission);
                            break;
                        case "hardwaresettings":
                            isAllowed =
                                !!(moduleClaim === null || moduleClaim === void 0 ? void 0 : moduleClaim.editPermission) &&
                                    !!(moduleClaim === null || moduleClaim === void 0 ? void 0 : moduleClaim.nodeHardwareSettingsPermission);
                            break;
                        default:
                            isAllowed = false;
                    }
                }
                return {
                    key: permission.key,
                    value: permission.value,
                    isAllowed: isAllowed,
                };
            });
            if (isEdit && (role === null || role === void 0 ? void 0 : role.id)) {
                // Update existing role
                const updateData = {
                    id: role.id,
                    name: data.name,
                    permissions: permissions,
                    isActive: role.isActive,
                    isSystemRole: false,
                    tenantId: "",
                };
                yield dispatch(updateRole(updateData))
                    .unwrap()
                    .then((response) => {
                    toast.success(response.message || t("common:toasts.roleUpdatedSuccessfully"));
                    // Refresh roles dropdown cache to reflect updated role name
                    dispatch(getRolesDropdown());
                    // Re-fetch the role to get fresh permissions from the server
                    dispatch(getRoleById(role.id));
                });
            }
            else {
                // Create new role
                const createData = {
                    name: data.name,
                    permissions: permissions,
                    isActive: true,
                };
                yield dispatch(createNewRole(createData))
                    .unwrap()
                    .then((response) => {
                    toast.success(response.message || t("common:toasts.roleCreatedSuccessfully"));
                    // Refresh roles dropdown cache to ensure it's available in other modules
                    dispatch(getRolesDropdown());
                    navigate("/roles");
                });
            }
        }
        catch (error) {
            toast.error(typeof error === "string" ? error : t("common:toasts.failedToSaveRole"));
        }
        finally {
            setLoading(false);
        }
    });
    const handlePermissionChange = (moduleName, permissionType, checked) => {
        // Prevent disabling Dashboard or AccessPoints View permission
        if ((moduleName === "Dashboard" || moduleName === "AccessPoints") &&
            permissionType === "viewPermission" &&
            !checked) {
            return; // Don't allow disabling these view permissions
        }
        const currentRoleClaims = methods.getValues("roleClaims") || [];
        const newRoleClaims = [...currentRoleClaims];
        const moduleIndex = newRoleClaims.findIndex((claim) => claim.module === moduleName);
        let updatedClaim;
        if (moduleIndex !== -1) {
            updatedClaim = Object.assign({}, newRoleClaims[moduleIndex]);
        }
        else {
            // Create new permission claim if it doesn't exist
            updatedClaim = {
                module: moduleName,
                viewPermission: moduleName === "Dashboard" || moduleName === "AccessPoints"
                    ? true
                    : false,
                createPermission: false,
                editPermission: false,
                deletePermission: false,
                serverSettingsPermission: false,
                nodeHardwareSettingsPermission: false,
            };
        }
        // Apply cascading permission logic
        if (checked) {
            // When enabling a permission, enable all lower-level permissions
            switch (permissionType) {
                case "deletePermission":
                    updatedClaim.deletePermission = true;
                    updatedClaim.editPermission = true;
                    updatedClaim.createPermission = true;
                    updatedClaim.viewPermission = true;
                    break;
                case "editPermission":
                    updatedClaim.editPermission = true;
                    updatedClaim.createPermission = true;
                    updatedClaim.viewPermission = true;
                    break;
                case "createPermission":
                    updatedClaim.createPermission = true;
                    updatedClaim.viewPermission = true;
                    break;
                case "viewPermission":
                    updatedClaim.viewPermission = true;
                    break;
            }
        }
        else {
            // When disabling a permission, disable all higher-level permissions
            switch (permissionType) {
                case "viewPermission":
                    updatedClaim.viewPermission = false;
                    updatedClaim.createPermission = false;
                    updatedClaim.editPermission = false;
                    updatedClaim.deletePermission = false;
                    break;
                case "createPermission":
                    updatedClaim.createPermission = false;
                    updatedClaim.editPermission = false;
                    updatedClaim.deletePermission = false;
                    break;
                case "editPermission":
                    updatedClaim.editPermission = false;
                    updatedClaim.deletePermission = false;
                    break;
                case "deletePermission":
                    updatedClaim.deletePermission = false;
                    break;
            }
        }
        if (moduleName === "AccessPoints" && !updatedClaim.editPermission) {
            updatedClaim.serverSettingsPermission = false;
        }
        if (moduleName === "Nodes" && !updatedClaim.editPermission) {
            updatedClaim.nodeHardwareSettingsPermission = false;
        }
        if (moduleIndex !== -1) {
            newRoleClaims[moduleIndex] = updatedClaim;
        }
        else {
            newRoleClaims.push(updatedClaim);
        }
        methods.setValue("roleClaims", newRoleClaims);
    };
    const setModuleExtendedPermission = (moduleName, field, value) => {
        const currentRoleClaims = methods.getValues("roleClaims") || [];
        const newRoleClaims = [...currentRoleClaims];
        const moduleIndex = newRoleClaims.findIndex((c) => c.module === moduleName);
        if (moduleIndex === -1)
            return;
        newRoleClaims[moduleIndex] = Object.assign(Object.assign({}, newRoleClaims[moduleIndex]), { [field]: value });
        methods.setValue("roleClaims", newRoleClaims);
    };
    // Update form values when role data changes (from API)
    useEffect(() => {
        if (role) {
            methods.reset({
                name: role.name,
                roleClaims: role.roleClaims,
            });
        }
    }, [role, methods]);
    // Initialize role claims with all modules if empty (for new role)
    useEffect(() => {
        if (!isEdit) {
            const currentRoleClaims = methods.getValues("roleClaims") || [];
            if (currentRoleClaims.length === 0) {
                const initialClaims = moduleOptions.map((module) => ({
                    module: String(module.value),
                    viewPermission: String(module.value) === "Dashboard" ||
                        String(module.value) === "AccessPoints"
                        ? true
                        : false,
                    createPermission: false,
                    editPermission: false,
                    deletePermission: false,
                    serverSettingsPermission: false,
                    nodeHardwareSettingsPermission: false,
                }));
                methods.setValue("roleClaims", initialClaims);
            }
        }
    }, [isEdit, methods]);
    return {
        methods,
        control,
        errors,
        handleSubmit,
        validationRules,
        handleFormSubmit,
        handlePermissionChange,
        setModuleExtendedPermission,
        loading,
    };
};
